import {getTestProps} from '@benchling/js-test-utils';
import React, {useMemo} from 'react';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  NodeDetailsSidepanelContext,
  NodeDetailsSidepanelContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import {
  NodeDetailsSidepanelContentBaseProps,
  SidepanelTab,
  TaskNodeDetailsSidepanelContentProps,
} from './processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractiveEndNode,
  isInteractivePlaceholderNode,
  isInteractiveRouterNode,
  isInteractiveTaskNode,
  isPlaceholderForAddExistingTaskSchemaData,
  isPlaceholderForTaskSchemaCreateData,
} from './processFlowchartConfigEditorNodeTypes';
import {PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE} from './processFlowchartConfigEditorRegistry';
import {LiveStateChanges} from './redux/FlowchartConfigStateManager';
import EndNodeDetailsSidepanelContent from './right-sidepanel/EndNodeDetailsSidepanelContent';
import PlaceholderNodeDetailsSidepanelContent from './right-sidepanel/PlaceholderNodeDetailsSidepanelContent';
import RouterNodeDetailsSidepanelContent from './right-sidepanel/RouterNodeDetailsSidepanelContent';
import TaskNodeDetailsSidepanelContent from './right-sidepanel/TaskNodeDetailsSidepanelContent';

type RightSidepanelProps = NodeDetailsSidepanelContentBaseProps &
  Omit<NodeDetailsSidepanelContextValue, 'selectedNode'> & {
    selectedNode: FlowchartNode | null;
    selectedFieldId: string | null;
    isOpen: boolean;
    platformFlowchartFeatures?: Map<ProcessFlowchartPlatformFeatureType, boolean>;
    onClose: () => void;
    size: SidepanelSize;
    titleOverride?: string;
    liveStateChanges: LiveStateChanges;
  };

function getSidepanelTabsForPlatformFeatures(
  platformFlowchartFeatures?: Map<ProcessFlowchartPlatformFeatureType, boolean>
): Array<SidepanelTab> {
  if (platformFlowchartFeatures == null) {
    return [];
  }
  const platformFeatureSidepanelTabs = [];
  for (const [feature, enabled] of platformFlowchartFeatures) {
    if (enabled && PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE[feature] != null) {
      platformFeatureSidepanelTabs.push(PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE[feature]);
    }
  }
  return platformFeatureSidepanelTabs;
}

function RightSidepanelContent(
  props: RightSidepanelProps & {selectedNode: FlowchartNode}
): JSX.Element | null {
  const {liveStateChanges} = props;
  const [isAutosaverPaused, setIsAutosaverPaused] = liveStateChanges.isAutosaverPausedState;
  const [isAutosaverBlocked, _setIsAutosaverBlocked] = liveStateChanges.isAutosaverBlockedState;
  const [isSaveInFlight, _setIsSaveInFlight] = liveStateChanges.isSaveInFlightState;
  const [isDirty, _setIsDirty] = liveStateChanges.dirtyState;
  const [isReadonly, _setIsReadonly] = liveStateChanges.readonlyState;

  if (isInteractiveTaskNode(props.selectedNode)) {
    const taskNodeData = props.selectedNode.data;

    const nodeDetailsSidepanelContentProps: TaskNodeDetailsSidepanelContentProps = {
      selectedNodeTaskDefinitionVersionSpec: taskNodeData.taskDefinitionVersionSpec,
      flowchart: props.flowchart,
      selectedFieldId: props.selectedFieldId,
      locked: props.locked,
      onUpdateTaskDefinitionVersion: props.onUpdateTaskDefinitionVersion,
      onChangeSelectedFieldId: props.onChangeSelectedFieldId,
      validationErrors: props.validationErrors,
      isAutosaverPaused,
      setIsAutosaverPaused,
      isAutosaverBlocked,
      isSaveInFlight,
      isDirty,
      isReadonly,
    };

    const tabs = [
      ...taskNodeData.nodeDetailsSidepanelContent,
      ...getSidepanelTabsForPlatformFeatures(props.platformFlowchartFeatures),
    ];

    return (
      <TaskNodeDetailsSidepanelContent
        nodeDetailsSidepanelContentProps={nodeDetailsSidepanelContentProps}
        tabs={tabs}
      />
    );
  } else if (isInteractivePlaceholderNode(props.selectedNode)) {
    return <PlaceholderNodeDetailsSidepanelContent selectedNode={props.selectedNode} />;
  } else if (isInteractiveRouterNode(props.selectedNode)) {
    return <RouterNodeDetailsSidepanelContent selectedNode={props.selectedNode} />;
  } else if (isInteractiveEndNode(props.selectedNode)) {
    return <EndNodeDetailsSidepanelContent />;
  }
  return null;
}

// TODO(BNCH-126721): Consolidate with transition modal right sidepanel
export default function ProcessFlowchartConfigEditorRightSidepanel(
  props: RightSidepanelProps
): JSX.Element | null {
  const {selectedNode, selectedFieldId, isOpen, onClose, ...contextProps} = props;

  const contextValue = useMemo((): NodeDetailsSidepanelContextValue | null => {
    if (!selectedNode) {
      return null;
    }

    return {
      ...contextProps,
      selectedNode,
    };
  }, [contextProps, selectedNode]);

  const getSidepanelTitle = (): string => {
    if (selectedNode == null) {
      return 'Node details';
    } else if (isInteractivePlaceholderNode(selectedNode)) {
      if (isPlaceholderForAddExistingTaskSchemaData(selectedNode.data.placeholderForNodeTypeData)) {
        return 'Add existing task schema';
      } else if (isPlaceholderForTaskSchemaCreateData(selectedNode.data.placeholderForNodeTypeData)) {
        return 'Create new task schema';
      } else {
        throw new Error('Unexpected placeholder type');
      }
    } else {
      return `Node ${selectedNode.data.nodeName} Details`;
    }
  };

  return (
    <FlowchartContextPanel
      panelSide={PanelSide.RIGHT}
      sidepanelName={props.titleOverride ?? getSidepanelTitle()}
      sidepanelIcon="settings"
      isOpen={isOpen && selectedNode != null}
      onClose={onClose}
      size={props.size}
      sidepanelKey="right"
    >
      {contextValue && selectedNode && (
        <NodeDetailsSidepanelContext.Provider key={selectedNode.id} value={contextValue}>
          <div {...getTestProps({component: 'ProcessFlowchartConfigEditorRightSidepanel', element: 'root'})}>
            <RightSidepanelContent {...props} selectedNode={selectedNode} />
          </div>
        </NodeDetailsSidepanelContext.Provider>
      )}
    </FlowchartContextPanel>
  );
}
